import {
  ClassificationIcon,
  NoData,
  RenderWhen,
  ThreeDotsLoader
} from "../../../../../components";
import { enums } from "../../../../../shim/jsbridge";
import { NetworkRepEntry } from "../../../../network/@types/Url";
import Name from "../../../../search/schema/components/Name";
import {
  DnsData,
  HttpData,
  NetworkItems,
  TcpUdpData,
  UrlData
} from "../../../@types/rldynamicanalysis";
import NetworkReferenceReputation, {
  NetworkReferenceReputationGridTitle
} from "../../report_summary/cards/network_references/NetworkReferenceReputation";
import {
  hasAssociatedMalware,
  isMalwareConfigC2,
  MalwareConfigAnnotationIcons
} from "../../malware_config";

const { analyzer: { URLANALYZER = 1 } = {} } = enums || {};

const ClassificationIconField = {
  name: "status",
  displayTitle: (
    <span style={{ opacity: "0.3" }} className="threat threat-placeholder" />
  ),
  sortable: false,
  component: (networkRepEntry: NetworkRepEntry) => (
    <ClassificationIcon classification={networkRepEntry.classification} />
  ),
  style: { width: "5%" },
  className: "threat-table-cell"
};

const ReputationField = {
  name: "reputation",
  displayTitle: <NetworkReferenceReputationGridTitle />,
  sortable: false,
  component: (networkRepEntry: NetworkRepEntry) => (
    <NetworkReferenceReputation networkRepEntry={networkRepEntry} />
  ),
  style: { width: "12%" }
};

const AnnotationIconsField = {
  name: "malware_config",
  displayTitle: "",
  sortable: false,
  component: (networkRepEntry: NetworkRepEntry) => (
    <MalwareConfigAnnotationIcons
      showMaliciousFiles={hasAssociatedMalware(networkRepEntry)}
      showMalwareConfig={isMalwareConfigC2(networkRepEntry)}
      variant="c2"
    />
  ),
  style: { width: "4%" }
};

const CategoriesField = {
  name: "categories",
  displayTitle: "Categories",
  sortable: false,
  component: ({
    categories,
    categoriesLoading
  }: NetworkRepEntry & { categoriesLoading?: boolean }) => (
    <RenderWhen condition={!categoriesLoading} fallback={<ThreeDotsLoader />}>
      {categories?.length ? (
        <span title={categories.join(", ")}>{categories.join(", ")}</span>
      ) : (
        <NoData />
      )}
    </RenderWhen>
  ),
  style: { width: "25%" },
  className: "overflow-ellipsis"
};

export const RlCloudSandboxNetworkItems: NetworkItems = {
  http: {
    title: "HTTP",
    schema: {
      fields: [
        ClassificationIconField,
        ReputationField,
        AnnotationIconsField,
        {
          name: "url",
          displayTitle: "URL",
          sortable: true,
          component: (d: HttpData & NetworkRepEntry) => (
            <Name
              data={{
                file_name: d.url ?? "",
                int_submission_analyzer: URLANALYZER,
                classification: d.classification
              }}
            />
          ),
          style: { width: "34%" }
        },
        {
          name: "method",
          displayTitle: "Method",
          sortable: true,
          component: (d: HttpData & NetworkRepEntry) => d.method,
          style: { width: "20%" }
        },
        CategoriesField
      ]
    }
  },
  tcp: {
    title: "TCP",
    schema: {
      fields: [
        ClassificationIconField,
        ReputationField,
        AnnotationIconsField,
        {
          name: "destination_ip",
          displayTitle: "Destination IP",
          sortable: true,
          component: (d: TcpUdpData & NetworkRepEntry) => (
            <Name
              data={{
                file_name: d.destination_ip ?? "",
                int_submission_analyzer: URLANALYZER,
                classification: d.classification
              }}
            />
          ),
          style: { width: "29%" }
        },
        {
          name: "destination_port",
          displayTitle: "Destination Port",
          sortable: true,
          component: (d: TcpUdpData & NetworkRepEntry) => d.destination_port,
          style: { width: "25%" }
        },
        CategoriesField
      ]
    }
  },
  udp: {
    title: "UDP",
    schema: {
      fields: [
        ClassificationIconField,
        ReputationField,
        AnnotationIconsField,
        {
          name: "destination_ip",
          displayTitle: "Destination IP",
          sortable: true,
          component: (d: TcpUdpData & NetworkRepEntry) => (
            <Name
              data={{
                file_name: d.destination_ip ?? "",
                int_submission_analyzer: URLANALYZER,
                classification: d.classification
              }}
            />
          ),
          style: { width: "29%" }
        },
        {
          name: "destination_port",
          displayTitle: "Destination Port",
          sortable: true,
          component: (d: TcpUdpData & NetworkRepEntry) => d.destination_port,
          style: { width: "25%" }
        },
        CategoriesField
      ]
    }
  },
  dns: {
    title: "DNS",
    schema: {
      fields: [
        ClassificationIconField,
        ReputationField,
        AnnotationIconsField,
        {
          name: "type",
          displayTitle: "Type",
          sortable: true,
          component: (d: DnsData & NetworkRepEntry) => d.type,
          style: { width: "10%" }
        },
        {
          name: "value",
          displayTitle: "Value",
          sortable: true,
          component: (d: DnsData & NetworkRepEntry) => (
            <Name
              data={{
                file_name: d.value ?? "",
                int_submission_analyzer: URLANALYZER,
                classification: d.classification
              }}
            />
          ),
          style: { width: "26%" }
        },
        {
          name: "ip",
          displayTitle: "IP",
          sortable: true,
          component: (d: DnsData & NetworkRepEntry) =>
            !d.address || d.address === "none" ? (
              <NoData />
            ) : (
              <Name
                data={{
                  file_name: d.address ?? "",
                  int_submission_analyzer: URLANALYZER
                }}
              />
            ),
          style: { width: "18%" }
        },
        CategoriesField
      ]
    }
  },
  url: {
    title: "URL",
    schema: {
      fields: [
        ClassificationIconField,
        ReputationField,
        AnnotationIconsField,
        {
          name: "url",
          displayTitle: "URL",
          sortable: true,
          component: (d: UrlData & NetworkRepEntry) => (
            <Name
              data={{
                file_name: d.url ?? "",
                int_submission_analyzer: URLANALYZER,
                classification: d.classification
              }}
            />
          ),
          style: { width: "34%" }
        },
        {
          name: "source",
          displayTitle: "Source",
          sortable: true,
          component: (d: UrlData & NetworkRepEntry) => d.source,
          style: { width: "20%" }
        },
        CategoriesField
      ]
    }
  }
};
